import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/ExamAttempt.css';
import ExerciseView from '../components/ExerciseView.js';
import ConfirmModal from '../components/ConfirmModal.js';
import ExamLayout from '../components/layouts/ExamLayout.js';

const ExamAttempt = ({ isReviewMode = false }) => {
  const { examId, attemptId } = useParams();
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState(null);
  
  const [examStatus, setExamStatus] = useState('overview'); // 'overview', 'in_progress', 'section_summary', 'exam_finished'
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [modalState, setModalState] = useState({
    isOpen: false,
    attemptId: null,
  });
  const [showNextSectionConfirmModal, setShowNextSectionConfirmModal] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [currentExerciseData, setCurrentExerciseData] = useState(null);

  const fetchExercise = async (exerciseId) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/client/exercises/${exerciseId}?mode=test`, {
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch exercise');
      }
      const data = await response.json();
      setCurrentExerciseData(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const getExistingAttempt = useCallback(async (attemptId) => {
    try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }
        const response = await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attemptId}`, {
            method: 'GET',
            headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to fetch existing exam attempt');
        }
        const data = await response.json();
        data.exam_structure.sort((a, b) => a.order_index - b.order_index);
        setAttempt(data);
    } catch (err) {
        setError(err.message);
    }
  }, []);

  const createAttempt = useCallback(async (forceNew) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exams/${examId}/attempts`, {
        method: 'POST',
        headers: { 
          'accept': 'application/json', 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force_new: forceNew })
      });

      if (response.status === 409) {
        const errorData = await response.json();
        setModalState({
            isOpen: true,
            attemptId: errorData.detail.attempt_id,
        });
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create exam attempt');
      }
      const data = await response.json();
      data.exam_structure.sort((a, b) => a.order_index - b.order_index);
      setAttempt(data);
    } catch (err) {
      setError(err.message);
    }
  }, [examId]);

  useEffect(() => {
    // If an attemptId is provided in the URL, fetch that attempt directly.
    if (attemptId) {
      getExistingAttempt(attemptId);
    } 
    // Otherwise, if an examId is provided, go through the creation process.
    else if (examId) {
      createAttempt(true);
    }
  }, [attemptId, examId, createAttempt, getExistingAttempt]);

  useEffect(() => {
    if (isReviewMode) {
      setExamStatus('in_progress');
    }
  }, [isReviewMode]);

  // Timer initialization effect
  useEffect(() => {
    if (examStatus === 'in_progress' && attempt) {
      const currentSection = attempt.exam_structure[currentSectionIndex];
      const durationInSeconds = currentSection.total_duration_minutes * 60;
      setTimeLeft(durationInSeconds);
    }
  }, [examStatus, currentSectionIndex, attempt]);

  // Countdown effect
  useEffect(() => {
    if (examStatus !== 'in_progress' || timeLeft <= 0) {
      return;
    }
    const intervalId = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [examStatus, timeLeft]);

  const handleContinue = () => {
    if (modalState.attemptId) {
        getExistingAttempt(modalState.attemptId);
    }
    setModalState({ isOpen: false, attemptId: null });
  };

  const handleStartNew = () => {
      createAttempt(true);
      setModalState({ isOpen: false, attemptId: null });
  };

  const handleStartExam = () => {
    const firstExerciseId = attempt.exam_structure[0].exercises[0].exercise_id;
    fetchExercise(firstExerciseId);
    setExamStatus('in_progress');
  };

  const handleNextExercise = () => {
    if (!attempt) return;
    const currentSection = attempt.exam_structure[currentSectionIndex];
    if (currentExerciseIndex < currentSection.exercises.length - 1) {
      const nextExerciseIndex = currentExerciseIndex + 1;
      const nextExerciseId = currentSection.exercises[nextExerciseIndex].exercise_id;
      fetchExercise(nextExerciseId);
      setCurrentExerciseIndex(nextExerciseIndex);
    } else {
      setExamStatus('section_summary');
    }
  };

  const handlePrevExercise = () => {
    if (!attempt) return;
    if (isReviewMode) {
      if (currentExerciseIndex > 0) {
        const prevExerciseIndex = currentExerciseIndex - 1;
        const prevExerciseId = attempt.exam_structure[currentSectionIndex].exercises[prevExerciseIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentExerciseIndex(prevExerciseIndex);
      } else if (currentSectionIndex > 0) {
        const prevSectionIndex = currentSectionIndex - 1;
        const prevSection = attempt.exam_structure[prevSectionIndex];
        const lastExerciseInPrevSectionIndex = prevSection.exercises.length - 1;
        const prevExerciseId = prevSection.exercises[lastExerciseInPrevSectionIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentSectionIndex(prevSectionIndex);
        setCurrentExerciseIndex(lastExerciseInPrevSectionIndex);
      }
    } else {
      if (currentExerciseIndex > 0) {
        const prevExerciseIndex = currentExerciseIndex - 1;
        const prevExerciseId = attempt.exam_structure[currentSectionIndex].exercises[prevExerciseIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentExerciseIndex(prevExerciseIndex);
      }
    }
  };

  const handleReviewSection = () => {
    setShowNextSectionConfirmModal(false);
    setExamStatus('in_progress');
  };

  const handleConfirmMoveToNextSection = async () => {
    if (!isReviewMode) {
      await saveSectionAnswers();
    }
    proceedToNextSection();
  };

  const handleCancelMoveToNextSection = () => {
    setShowNextSectionConfirmModal(false);
  };

  const saveSectionAnswers = async () => {
    if (!attempt) return;
    const currentSection = attempt.exam_structure[currentSectionIndex];
    const attemptKey = `attempt_${attempt.attempt_id}`;
    let allSavedAnswers = {};

    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        allSavedAnswers = JSON.parse(savedAttempt);
      }
    } catch (e) {
      console.error("Failed to parse saved attempt data from localStorage:", e);
      // If parsing fails, we'll start with an empty object for answers
    }

    const answersToSubmit = [];
    currentSection.exercises.forEach(exercise => {
      const exerciseAnswers = allSavedAnswers[exercise.exercise_id];
      if (exerciseAnswers) {
        // exerciseAnswers is an object where keys are question_id
        Object.keys(exerciseAnswers).forEach(questionId => {
          const answerData = exerciseAnswers[questionId];
          if (answerData) {
            answersToSubmit.push({
              question_id: questionId,
              user_answer: [JSON.stringify(answerData)] // Stringify the whole answer data object
            });
          }
        });
      }
    });

    console.log("Submitting answers for current section:", answersToSubmit);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attempt.attempt_id}/answers`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: answersToSubmit, exam_section_id: currentSection.section_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save section answers');
      }
      console.log("Section answers saved successfully.");

      // Clear the section's answers from local storage after successful submission
      currentSection.exercises.forEach(exercise => {
        delete allSavedAnswers[exercise.exercise_id];
      });
      localStorage.setItem(attemptKey, JSON.stringify(allSavedAnswers));
    } catch (err) {
      setError(err.message);
      console.error("Error saving section answers:", err);
    }
  };

  const proceedToNextSection = async () => {
    if (!attempt) return;
    if (currentSectionIndex < attempt.exam_structure.length - 1) {
      const nextSectionIndex = currentSectionIndex + 1;
      const firstExerciseId = attempt.exam_structure[nextSectionIndex].exercises[0].exercise_id;
      fetchExercise(firstExerciseId);
      setCurrentSectionIndex(nextSectionIndex);
      setCurrentExerciseIndex(0);
      setExamStatus('in_progress');
    } else {
      setExamStatus('exam_finished');
    }
    setShowNextSectionConfirmModal(false);
  };

  const handleNextSection = () => {
    if (isReviewMode) {
      proceedToNextSection();
    } else {
      setShowNextSectionConfirmModal(true);
    }
  };

  // --- Loading and Error States ---
  if (error) {
    return <div className="exam-attempt-container"><div className="error-message">Error: {error}</div></div>;
  }
  if (!attempt) {
    return (
        <>
            <ConfirmModal
                isOpen={modalState.isOpen}
                onConfirm={handleContinue}
                onDeny={handleStartNew}
                title="Lượt làm bài chưa hoàn thành"
                message="Bạn có một bài kiểm tra chưa hoàn thành. Bạn muốn tiếp tục làm hay bắt đầu một lượt mới?"
                confirmText="Tiếp Tục làm"
                denyText="Làm lượt mới"
            />
            <div className="exam-attempt-container"><p>Creating exam attempt...</p></div>
        </>
    );
  }

  // --- Render Logic ---

  return (
    <>
      <ConfirmModal
          isOpen={showNextSectionConfirmModal}
          onConfirm={handleConfirmMoveToNextSection}
          onDeny={handleCancelMoveToNextSection}
          title="Xác nhận chuyển phần thi"
          message="Bạn có chắc chắn muốn chuyển sang phần kế tiếp? Bài làm của bạn sẽ được gửi lên server và bạn không thể chỉnh sửa phần này khi đã bắt đầu làm phần kế tiếp."
          confirmText="Xác nhận"
          denyText="Hủy"
      />
      {examStatus === 'overview' && (() => {
        // Find the current section from the structure
        const currentSectionDetails = attempt.exam_structure.find(s => s.section_id === attempt.current_section_id);
        // Get its order index. If not found, it means no section has been started.
        const currentOrderIndex = currentSectionDetails ? currentSectionDetails.order_index : -1;

        return (
          <div className="exam-attempt-container">
            <div className="exam-overview-header"><h1>Exam Overview</h1></div>
            <div className="exam-sections-list">
              <h2>Các phần thi</h2>
              {attempt.exam_structure.map(section => {
                const status = section.order_index <= currentOrderIndex ? 'ĐÃ hoàn thành' : 'Chưa hoàn thành';
                const statusClassName = section.order_index <= currentOrderIndex ? 'status-completed' : 'status-not-completed';

                return (
                  <div key={section.section_id} className="section-card">
                    <h3>{section.section_title}</h3>
                    <div className="section-details">
                      <p><strong>Số câu hỏi:</strong> {section.total_questions}</p>
                      <p><strong>Thời gian:</strong> {section.total_duration_minutes} phút</p>
                      <p><strong>Điểm tối đa:</strong> {section.max_score}</p>
                      <p><strong>Trạng thái:</strong> <span className={statusClassName}>{status}</span></p>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="start-exam-container">
              <button onClick={handleStartExam} className="btn btn-primary btn-large">Bắt đầu làm bài</button>
            </div>
          </div>
        )
      })()}

      {examStatus === 'in_progress' && currentExerciseData && (
        <ExamLayout
          sectionTitle={attempt.exam_structure[currentSectionIndex].section_title}
          timeLeft={timeLeft}
          onPrev={handlePrevExercise}
          onNext={handleNextExercise}
          onShowHint={() => setShowHint(!showHint)}
          isPrevDisabled={isReviewMode ? (currentSectionIndex === 0 && currentExerciseIndex === 0) : (currentExerciseIndex === 0)}
        >
          <ExerciseView
            exercise={currentExerciseData}
            attemptId={attempt.attempt_id}
            showHint={showHint}
            isReviewMode={isReviewMode}
          />
        </ExamLayout>
      )}

      {examStatus === 'section_summary' && (
        <div className="exam-attempt-container">
          <div className="section-summary">
            <h2>Kết thúc phần thi: {attempt.exam_structure[currentSectionIndex].section_title}</h2>
            <p>You have completed this section.</p>
            <p className="section-warning-message">Lưu ý: Bạn không thể quay lại phần này sau khi chuyển sang phần tiếp theo.</p>
            <div className="section-summary-actions">
              <button onClick={handleReviewSection} className="btn">Review</button>
              <button onClick={handleNextSection} className="btn btn-primary">
                {currentSectionIndex === attempt.exam_structure.length - 1 ? 'Finish Exam' : 'Bắt đầu phần thi tiếp theo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {examStatus === 'exam_finished' && (
        <div className="exam-attempt-container">
          <div className="exam-finished">
            <h2>Congratulations!</h2>
            <p>You have completed the exam.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ExamAttempt;